import React, { useState } from 'react'

export default function ContactForm({ variant = 'light' }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot field
  })

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  })

  const [errors, setErrors] = useState({})

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = 'Navn skal udfyldes'
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer skal udfyldes'
    } else if (!/^\+?[0-9\s]{8,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Ugyldigt telefonnummer'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email skal udfyldes'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ugyldig emailadresse'
    }
    if (!formData.message.trim()) newErrors.message = 'Besked skal udfyldes'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setStatus({ submitting: true, success: false, error: null })

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null })
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: ''
        })
      } else {
        const errData = await response.json()
        setStatus({
          submitting: false,
          success: false,
          error: errData.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.'
        })
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: 'Netværksfejl. Kontroller din internetforbindelse og prøv igen.'
      })
    }
  }

  const isDark = variant === 'dark'

  return (
    <div className={isDark ? "w-full" : "rounded-2xl border border-gray-200 bg-white p-6 shadow-xl md:p-8"}>
      {!isDark && <h3 className="mb-6 text-2xl font-bold text-gray-900 md:text-3xl">Skriv til os</h3>}
      
      {status.success ? (
        <div className={isDark 
          ? "rounded-xl bg-white/10 backdrop-blur-sm p-8 text-center text-white border border-white/20"
          : "rounded-xl bg-emerald-50 p-6 text-center text-emerald-800 border border-emerald-200"
        }>
          <i className={`fa-regular fa-circle-check text-4xl mb-3 animate-bounce ${isDark ? 'text-brand-red' : 'text-emerald-600'}`} />
          <h4 className="text-xl font-bold mb-2">Tak for din besked!</h4>
          <p className={`text-sm ${isDark ? 'text-gray-200' : 'text-emerald-700'}`}>
            Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute -top-9999 -left-9999" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this out if you are a human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {isDark ? (
            <>
              {/* Top row: splits into Name and Email using a 50/50 grid */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Navn */}
                <div>
                  <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-white/80 mb-1">
                    Navn <span className="text-brand-red">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full bg-transparent border-b py-2.5 text-white placeholder-white/40 focus:outline-none focus:border-brand-red transition-colors text-sm rounded-none shadow-none ${
                      errors.name ? 'border-red-500' : 'border-white/30'
                    }`}
                    placeholder="Dit fulde navn"
                    disabled={status.submitting}
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-400 font-medium">{errors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-white/80 mb-1">
                    Email <span className="text-brand-red">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full bg-transparent border-b py-2.5 text-white placeholder-white/40 focus:outline-none focus:border-brand-red transition-colors text-sm rounded-none shadow-none ${
                      errors.email ? 'border-red-500' : 'border-white/30'
                    }`}
                    placeholder="din@email.dk"
                    disabled={status.submitting}
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-400 font-medium">{errors.email}</p>}
                </div>
              </div>

              {/* Subsequent fields: Phone (full width) */}
              <div>
                <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-white/80 mb-1">
                  Telefonnummer <span className="text-brand-red">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full bg-transparent border-b py-2.5 text-white placeholder-white/40 focus:outline-none focus:border-brand-red transition-colors text-sm rounded-none shadow-none ${
                    errors.phone ? 'border-red-500' : 'border-white/30'
                  }`}
                  placeholder="Tlf. nr."
                  disabled={status.submitting}
                />
                {errors.phone && <p className="mt-1 text-xs text-red-400 font-medium">{errors.phone}</p>}
              </div>

              {/* Subsequent fields: Message (full width) */}
              <div>
                <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-white/80 mb-1">
                  Besked <span className="text-brand-red">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  className={`w-full bg-transparent border-b py-2.5 text-white placeholder-white/40 focus:outline-none focus:border-brand-red transition-colors text-sm rounded-none shadow-none resize-none ${
                    errors.message ? 'border-red-500' : 'border-white/30'
                  }`}
                  placeholder="Beskriv din opgave eller dit spørgsmål her..."
                  disabled={status.submitting}
                />
                {errors.message && <p className="mt-1 text-xs text-red-400 font-medium">{errors.message}</p>}
              </div>
            </>
          ) : (
            <>
              {/* Navn */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Navn <span className="text-brand-red">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-4 py-3 text-gray-900 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red ${
                    errors.name ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                  }`}
                  placeholder="Dit fulde navn"
                  disabled={status.submitting}
                />
                {errors.name && <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.name}</p>}
              </div>

              {/* Telefonnummer & Email (Grid on desktop) */}
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Telefonnummer <span className="text-brand-red">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full rounded-lg border px-4 py-3 text-gray-900 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red ${
                      errors.phone ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                    }`}
                    placeholder="Tlf. nr."
                    disabled={status.submitting}
                  />
                  {errors.phone && <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.phone}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Email <span className="text-brand-red">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full rounded-lg border px-4 py-3 text-gray-900 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red ${
                      errors.email ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                    }`}
                    placeholder="din@email.dk"
                    disabled={status.submitting}
                  />
                  {errors.email && <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.email}</p>}
                </div>
              </div>

              {/* Besked */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Besked <span className="text-brand-red">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-4 py-3 text-gray-900 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red ${
                    errors.message ? 'border-red-500 bg-red-50/30' : 'border-gray-300'
                  }`}
                  placeholder="Beskriv din opgave eller dit spørgsmål her..."
                  disabled={status.submitting}
                />
                {errors.message && <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.message}</p>}
              </div>
            </>
          )}

          {status.error && (
            <div className={`rounded-lg p-4 text-sm border flex items-start gap-2 ${
              isDark ? 'bg-red-950/40 text-red-200 border-red-900/50' : 'bg-red-50 text-red-800 border-red-200'
            }`}>
              <i className="fa-regular fa-triangle-exclamation mt-0.5" />
              <span>{status.error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status.submitting}
            className={`w-full px-6 py-3.5 text-center text-sm font-bold text-white shadow-md hover:bg-brand-red-hover transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
              isDark ? 'rounded-full bg-brand-red' : 'rounded-lg bg-brand-red'
            }`}
          >
            {status.submitting ? (
              <>
                <i className="fa-regular fa-spinner animate-spin" />
                Sender besked...
              </>
            ) : (
              <>
                <i className="fa-regular fa-paper-plane" />
                Send besked
              </>
            )}
          </button>
        </form>
      )}
    </div>
  )
}
